/**
 * Storefront-API client (ADR-011 v2).
 *
 * - openapi-fetch met gegenereerde types uit `openapi.json` (zie `npm run gen-types`).
 * - Mock-mode via VITE_MOCK_API=true → routet naar public/mock/*.json.
 * - Twee clients: `anonClient` (geen Authorization-header → Cloudflare-cacheable)
 *   en `authedClient` (Bearer JWT uit localStorage).
 *
 * NB: zolang `openapi.json` nog niet gepubliceerd is in deze repo (W3 → W2 in monorepo),
 * werken we met handgeschreven minimale typing. Vervang dit door `paths` uit
 * `src/types/storefront-api.ts` zodra de eerste contract-sync PR binnen komt.
 */
import { mockFetch, isMockApiEnabled } from "./mock";
import { ApiError } from "./errors";

export { ApiError, isApiError } from "./errors";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const JWT_STORAGE_KEY = "showcase_jwt";

export function getStoredJwt(): string | null {
  try {
    return localStorage.getItem(JWT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredJwt(token: string | null): void {
  try {
    if (token) localStorage.setItem(JWT_STORAGE_KEY, token);
    else localStorage.removeItem(JWT_STORAGE_KEY);
  } catch {
    /* ignore (e.g. private mode) */
  }
}

function buildHeaders(needsAuth: boolean, contentType?: string): Headers {
  const h = new Headers({ Accept: "application/json" });
  if (contentType) h.set("Content-Type", contentType);
  if (needsAuth) {
    const jwt = getStoredJwt();
    if (jwt) h.set("Authorization", `Bearer ${jwt}`);
  }
  return h;
}

async function request(
  path: string,
  init: RequestInit & { needsAuth?: boolean } = {}
): Promise<unknown> {
  if (isMockApiEnabled() && (init.method === undefined || init.method === "GET")) {
    return mockFetch(path);
  }

  const { needsAuth, headers: extra, ...rest } = init;
  const headers = buildHeaders(
    needsAuth ?? false,
    init.body ? "application/json" : undefined
  );
  if (extra) {
    new Headers(extra).forEach((v, k) => headers.set(k, v));
  }

  const resp = await fetch(`${API_BASE}${path}`, { ...rest, headers });

  if (resp.status === 401 && needsAuth) {
    // Verlopen JWT → wis state en laat consument om-redirecten naar /login
    setStoredJwt(null);
  }

  if (!resp.ok) {
    let payload: unknown = null;
    try {
      payload = await resp.json();
    } catch {
      /* no body */
    }
    throw new ApiError(resp.status, payload);
  }

  if (resp.status === 204) return null;
  return resp.json();
}

export const anonClient = {
  get: (path: string) => request(path),
};

export const authedClient = {
  get: (path: string) => request(path, { needsAuth: true }),
  post: (path: string, body: unknown) =>
    request(path, {
      method: "POST",
      needsAuth: true,
      body: JSON.stringify(body),
    }),
};

